import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * Éditeur de retouche par zone — on peint la zone au pinceau, on décrit ce
 * qu'on veut à cet endroit, et seule cette zone est régénérée.
 *
 * Props :
 *  - src : URL complète de l'image à retoucher
 *  - onCancel : fermeture sans rien lancer
 *  - onSubmit : ({ maskPng, prompt }) => Promise — l'éditeur reste ouvert si
 *    la promesse échoue, pour ne pas faire perdre les traits déjà peints.
 *
 * Deux canvas volontairement séparés :
 *  - celui du MASQUE est à la résolution NATIVE de l'image et strictement
 *    noir/blanc (blanc = à repeindre). Le fournisseur et le backend refusent
 *    tout autre format, et un masque à la taille d'AFFICHAGE serait rejeté.
 *  - celui de la SURIMPRESSION est à la taille affichée, en couleur d'accent.
 *    Il est rendu opaque puis rendu translucide par CSS : deux traits qui se
 *    croisent gardent ainsi la même teinte au lieu de s'assombrir.
 *
 * L'image source n'est jamais dessinée dans le canvas du masque : celui-ci
 * reste donc « propre » au sens CORS et `toDataURL` fonctionne même si le
 * média vient d'une autre origine que le front.
 */
const MIN_BRUSH = 4;
const MAX_BRUSH = 200;

const paintStroke = (ctx, stroke, scale, target, accent) => {
  const width = Math.max(1, stroke.size * scale);
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.lineWidth = width;
  if (target === 'mask') {
    // Strictement noir et blanc : c'est ce que le fournisseur attend.
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = stroke.erase ? '#000000' : '#ffffff';
  } else {
    // La gomme retire vraiment les pixels de la surimpression (l'opacité de
    // `destination-out` seule compte, pas la couleur).
    ctx.globalCompositeOperation = stroke.erase ? 'destination-out' : 'source-over';
    ctx.strokeStyle = stroke.erase ? '#000000' : accent;
  }
  ctx.fillStyle = ctx.strokeStyle;

  const pts = stroke.points;
  if (pts.length < 2) {
    // Un simple clic doit peindre un point, pas rien.
    ctx.beginPath();
    ctx.arc(pts[0].x * scale, pts[0].y * scale, width / 2, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.moveTo(pts[0].x * scale, pts[0].y * scale);
    for (let i = 1; i < pts.length; i += 1) ctx.lineTo(pts[i].x * scale, pts[i].y * scale);
    ctx.stroke();
  }
  ctx.restore();
};

const MaskEditor = ({ src, onCancel, onSubmit }) => {
  const [natural, setNatural] = useState(null);      // résolution native {w, h}
  const [box, setBox] = useState({ w: 0, h: 0 });    // taille affichée
  const [strokes, setStrokes] = useState([]);        // pile de TRAITS, pas d'images
  const [brush, setBrush] = useState(40);            // diamètre en px affichés
  const [erasing, setErasing] = useState(false);
  const [prompt, setPrompt] = useState('');
  // Intensité réellement honorée par l'endpoint : mesurée à 79 % de changement
  // dans la zone masquée par défaut, contre 4,4 % à 0.05. Défaut haut : on
  // attend d'une retouche qu'elle remplace, pas qu'elle effleure.
  const [strength, setStrength] = useState(0.85);
  const [busy, setBusy] = useState(false);

  const imgRef = useRef(null);
  const viewRef = useRef(null);
  const maskRef = useRef(null);
  const activeRef = useRef(null);
  const accentRef = useRef('#63b3a4');
  const onCancelRef = useRef(onCancel);

  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);

  // Canvas hors écran créé à la demande (pas à chaque rendu).
  const getMask = () => {
    if (!maskRef.current) maskRef.current = document.createElement('canvas');
    return maskRef.current;
  };

  // La couleur d'accent vit dans les jetons CSS : on la lit une fois pour
  // pouvoir peindre dans un canvas (qui n'interprète pas var(--accent)).
  useEffect(() => {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim();
    if (v) accentRef.current = v;
  }, []);

  // Plein écran : on gèle le défilement du fond et Échap referme, comme la Lightbox.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCancelRef.current?.();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        setStrokes((prev) => prev.slice(0, -1));
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const readNatural = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return;
    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    setBox({ w: img.clientWidth, h: img.clientHeight });
  }, []);

  // L'image peut déjà être en cache : `onLoad` ne se déclencherait jamais.
  useEffect(() => {
    if (imgRef.current?.complete) readNatural();
  }, [src, readNatural]);

  // La taille affichée change avec la fenêtre ; les traits sont stockés en
  // coordonnées natives, donc seul le facteur d'échelle du rendu bouge.
  useEffect(() => {
    const img = imgRef.current;
    if (!img || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setBox({ w: img.clientWidth, h: img.clientHeight }));
    ro.observe(img);
    return () => ro.disconnect();
  }, [natural]);

  // Le canvas du masque est dimensionné en NATIF, une fois l'image connue.
  useEffect(() => {
    if (!natural) return;
    const mask = getMask();
    mask.width = natural.w;
    mask.height = natural.h;
  }, [natural]);

  const redraw = useCallback(() => {
    if (!natural) return;
    const mask = getMask();
    const view = viewRef.current;
    const mctx = mask.getContext('2d');
    mctx.globalCompositeOperation = 'source-over';
    mctx.fillStyle = '#000000';
    mctx.fillRect(0, 0, mask.width, mask.height);
    strokes.forEach((s) => paintStroke(mctx, s, 1, 'mask'));

    if (!view || !view.width) return;
    const vctx = view.getContext('2d');
    vctx.clearRect(0, 0, view.width, view.height);
    const scale = view.width / natural.w;
    strokes.forEach((s) => paintStroke(vctx, s, scale, 'view', accentRef.current));
  }, [natural, strokes]);

  useEffect(() => { redraw(); }, [redraw, box]);

  // Conversion pointeur -> pixels NATIFS de l'image. `getBoundingClientRect`
  // donne la taille CSS réelle, seule référence correcte : le canvas est
  // affiché en réduit, le masque doit sortir aux dimensions natives.
  const toNative = (e, rect) => ({
    x: ((e.clientX - rect.left) / rect.width) * natural.w,
    y: ((e.clientY - rect.top) / rect.height) * natural.h,
  });

  // Rendu incrémental du dernier segment : évite un redessin complet du
  // canvas natif à chaque mouvement du pointeur.
  const paintTail = (stroke) => {
    const tail = { ...stroke, points: stroke.points.slice(-2) };
    paintStroke(getMask().getContext('2d'), tail, 1, 'mask');
    const view = viewRef.current;
    if (view?.width) {
      paintStroke(view.getContext('2d'), tail, view.width / natural.w, 'view', accentRef.current);
    }
  };

  const onPointerDown = (e) => {
    if (!natural || busy) return;
    e.preventDefault();
    const view = viewRef.current;
    try { view.setPointerCapture(e.pointerId); } catch { /* pointeur déjà relâché */ }
    const rect = view.getBoundingClientRect();
    activeRef.current = {
      points: [toNative(e, rect)],
      // Le diamètre est saisi en pixels affichés, stocké en pixels natifs :
      // un redimensionnement de la fenêtre ne déforme pas les traits déjà faits.
      size: brush * (natural.w / rect.width),
      erase: erasing,
    };
    paintTail(activeRef.current);
  };

  const onPointerMove = (e) => {
    const active = activeRef.current;
    if (!active) return;
    e.preventDefault();
    active.points.push(toNative(e, viewRef.current.getBoundingClientRect()));
    paintTail(active);
  };

  const endStroke = (e) => {
    const active = activeRef.current;
    if (!active) return;
    activeRef.current = null;
    try { viewRef.current.releasePointerCapture(e.pointerId); } catch { /* déjà relâché */ }
    setStrokes((prev) => [...prev, active]);   // déclenche un redessin propre
  };

  const maskHasPaint = () => {
    const mask = getMask();
    if (!mask.width) return false;
    const { data } = mask.getContext('2d').getImageData(0, 0, mask.width, mask.height);
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] > 127) return true;
    }
    return false;
  };

  const canSubmit = natural && strokes.length > 0 && prompt.trim().length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    // Une pile de traits entièrement gommés laisse un masque noir : le
    // backend le refuserait, autant ne pas faire l'aller-retour.
    if (!maskHasPaint()) {
      toast.warning('Aucune zone peinte — le masque est vide.');
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ maskPng: getMask().toDataURL('image/png'), prompt: prompt.trim(), strength });
    } catch {
      /* le parent affiche l'erreur ; on reste ouvert pour garder les traits */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mask-overlay" data-testid="mask-editor">
      <div
        className="mask-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Retoucher une zone de l'image"
      >
        <div className="mask-head">
          <h2 className="mask-title">Retoucher une zone</h2>
          <button type="button" className="mask-close" onClick={onCancel}
            aria-label="Fermer l'éditeur de retouche">✕</button>
        </div>

        <p className="mask-hint">
          Peignez la zone à régénérer, puis décrivez ce que vous voulez à cet endroit.
          Le reste de l'image est conservé pixel pour pixel.
        </p>

        <div className="mask-stage">
          <img
            ref={imgRef}
            src={src}
            alt=""
            className="mask-image"
            draggable={false}
            onLoad={readNatural}
          />
          <canvas
            ref={viewRef}
            className="mask-canvas"
            width={box.w}
            height={box.h}
            style={{ width: `${box.w}px`, height: `${box.h}px` }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endStroke}
            onPointerCancel={endStroke}
          />
        </div>

        <div className="mask-tools">
          <label className="mask-brush" htmlFor="mask-brush-size">
            brosse
            <input
              id="mask-brush-size"
              type="range"
              min={MIN_BRUSH}
              max={MAX_BRUSH}
              step="2"
              value={brush}
              onChange={(e) => setBrush(parseInt(e.target.value, 10))}
            />
            <span className="mask-brush-value">{brush}px</span>
          </label>
          <button
            type="button"
            className={`btn btn-outline${erasing ? ' mask-tool-on' : ''}`}
            aria-pressed={erasing}
            onClick={() => setErasing((v) => !v)}
          >
            Gomme
          </button>
          <button type="button" className="btn btn-outline"
            onClick={() => setStrokes((prev) => prev.slice(0, -1))}
            disabled={strokes.length === 0}>
            Annuler le trait
          </button>
          <button type="button" className="btn btn-outline"
            onClick={() => setStrokes([])} disabled={strokes.length === 0}>
            Tout effacer
          </button>
          <span className="mask-meta">
            {natural ? `${natural.w}×${natural.h}` : 'chargement…'}
            {strokes.length > 0 && ` · ${strokes.length} trait${strokes.length > 1 ? 's' : ''}`}
          </span>
        </div>

        <label className="form-label mask-prompt-label" htmlFor="mask-prompt">
          Ce que vous voulez à cet endroit
        </label>
        <textarea
          id="mask-prompt"
          className="form-textarea mask-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="a brass desk lamp, warm light…"
          data-testid="mask-prompt"
        />

        <div className="mask-strength">
          <label className="form-hint" htmlFor="mask-strength">
            Intensité de la retouche : {strength.toFixed(2)}
          </label>
          <input id="mask-strength" type="range" min="0.1" max="1" step="0.05"
            value={strength} onChange={(e) => setStrength(parseFloat(e.target.value))}
            data-testid="mask-strength" />
          <span className="form-hint">
            Bas : la zone est seulement retouchée. Haut : elle est remplacée.
          </span>
        </div>

        <div className="mask-actions">
          <button type="button" className="btn btn-outline" onClick={onCancel}>Annuler</button>
          <button type="button" className="btn btn-primary" onClick={submit}
            disabled={!canSubmit} data-testid="mask-submit">
            {busy ? 'Envoi…' : 'Retoucher'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaskEditor;
